import React, { useState, useEffect, useCallback } from 'react';
import { puantajQualityApi } from '../api/puantajQualityApi';
import { useAuth } from '../contexts/AuthContext';
import {
    AlertTriangle,
    CheckCircle,
    RefreshCw,
    ChevronRight,
    ShieldAlert,
    Zap,
    ArrowUpRight,
    ArrowDownRight,
    UserCheck,
    Clock,
    Calendar,
    CalendarOff
} from 'lucide-react';
import BatchCorrectionModal from './BatchCorrectionModal';

const formatLocalDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getYesterdaysDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return formatLocalDate(d);
};

const getTodaysDate = () => formatLocalDate(new Date());

export function QualityDashboardWidget({ isEditMode = false, onActionComplete }) {
    const { hasPermission } = useAuth();
    const [date, setDate] = useState(getYesterdaysDate());
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [initialModalAction, setInitialModalAction] = useState(null);

    const fetchSummary = useCallback(async (targetDate = date) => {
        if (!hasPermission('puantaj_quality:dashboard_widget')) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await puantajQualityApi.getDashboardSummary(targetDate);
            setSummary(data);
        } catch (err) {
            console.error(err);
            setError('Veri yüklenemedi.');
        } finally {
            setLoading(false);
        }
    }, [date, hasPermission]);

    useEffect(() => {
        fetchSummary(date);
    }, [date, fetchSummary]);

    if (!hasPermission('puantaj_quality:dashboard_widget')) {
        return null;
    }

    const openModalWithAction = (actionId = null) => {
        if (isEditMode) return;
        setInitialModalAction(actionId);
        setIsModalOpen(true);
    };

    if (loading) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-4 flex flex-col justify-center items-center h-full animate-pulse">
                <RefreshCw className="w-7 h-7 text-indigo-500 animate-spin mb-2" />
                <span className="text-gray-400 dark:text-gray-500 text-xs font-bold uppercase tracking-widest">
                    Kalite Verileri Analiz Ediliyor...
                </span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-red-100 dark:border-red-900/30 p-4 flex flex-col justify-between h-full">
                <div className="flex items-center text-red-600 dark:text-red-400 gap-2 font-bold text-sm">
                    <AlertTriangle size={18} />
                    <span>Puantaj Kalite Özeti</span>
                </div>
                <div className="text-red-500 dark:text-red-400 text-xs bg-red-50 dark:bg-red-900/10 p-3 rounded-lg border border-red-100 dark:border-red-900/20">
                    {error}
                </div>
                <button
                    onClick={() => fetchSummary(date)}
                    className="self-start text-xs font-bold bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg transition-all"
                >
                    Tekrar Dene
                </button>
            </div>
        );
    }

    const hasIssues = summary && summary.totalEmployeesWithIssues > 0;
    const leaveConflictCount = summary?.leaveConflictCount || 0;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-4 flex flex-col justify-between relative overflow-hidden group transition-all duration-300 hover:shadow-2xl h-full min-h-0">
            {/* Background Accent */}
            <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-5 pointer-events-none transition-transform group-hover:scale-150 duration-700 ${hasIssues ? 'bg-amber-500' : 'bg-emerald-500'}`} />

            {/* Header */}
            <div className="flex flex-wrap justify-between items-center gap-2 pb-2.5 border-b border-gray-100 dark:border-gray-700/70 shrink-0">
                <div className="flex flex-wrap items-center gap-2.5">
                    <div className={`p-1.5 rounded-lg ${hasIssues ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'}`}>
                        {hasIssues ? <ShieldAlert size={18} /> : <UserCheck size={18} />}
                    </div>
                    <h3 className="font-bold text-gray-800 dark:text-gray-100 text-sm leading-tight">
                        Puantaj Riskleri ve Kalite Kontrol
                    </h3>
                    <div className="flex items-center gap-1 ml-1">
                        <Calendar size={11} className="text-gray-400" />
                        <input
                            type="date"
                            value={date}
                            disabled={isEditMode}
                            onChange={(e) => e.target.value && setDate(e.target.value)}
                            className="text-[11px] font-bold text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer"
                        />
                        <button
                            type="button"
                            disabled={isEditMode}
                            onClick={() => setDate(getYesterdaysDate())}
                            className={`text-[10px] px-2 py-0.5 rounded font-bold transition-colors ${date === getYesterdaysDate() ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'}`}
                        >
                            Dün
                        </button>
                        <button
                            type="button"
                            disabled={isEditMode}
                            onClick={() => setDate(getTodaysDate())}
                            className={`text-[10px] px-2 py-0.5 rounded font-bold transition-colors ${date === getTodaysDate() ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'}`}
                        >
                            Bugün
                        </button>
                    </div>
                </div>

                <span className={`text-[11px] px-2.5 py-0.5 rounded-full font-black uppercase tracking-wider shadow-sm ${
                    hasIssues
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800/50'
                        : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/50'
                }`}>
                    {hasIssues ? `${summary.totalEmployeesWithIssues} Hatalı Personel` : 'Sorunsuz'}
                </span>
            </div>

            {/* Body Content — Yatay 5'li Kompakt Grid ile taşma olmadan tam sığar */}
            <div className="flex-1 min-h-0 overflow-y-auto py-2.5 flex flex-col justify-center">
                {!hasIssues ? (
                    <div className="flex items-center justify-center gap-3 py-4 px-4 text-center bg-gray-50/50 dark:bg-gray-900/20 rounded-xl border border-dashed border-gray-200 dark:border-gray-700/50">
                        <CheckCircle className="w-6 h-6 text-emerald-500 dark:text-emerald-400 shrink-0" />
                        <p className="text-xs text-gray-600 dark:text-gray-300 font-medium">
                            Seçili tarihte ({new Date(date).toLocaleDateString('tr-TR')}) açık puantaj hatası veya izin çakışması bulunmuyor.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
                        {/* 1. İzinli Günde Geçiş Kaydı */}
                        <div
                            onClick={() => openModalWithAction(6)}
                            title="İzinli olduğu halde kart okutan personelleri incelemek ve toplu düzeltmek için tıklayın"
                            className={`col-span-2 md:col-span-1 p-3 rounded-xl border flex flex-col justify-between transition-all cursor-pointer ${
                                leaveConflictCount > 0
                                    ? 'bg-rose-50/90 dark:bg-rose-900/25 border-rose-200 dark:border-rose-800/60 hover:bg-rose-100/80 dark:hover:bg-rose-900/40 shadow-sm'
                                    : 'bg-gray-50/80 dark:bg-gray-900/40 border-gray-100 dark:border-gray-700/50 hover:border-gray-200'
                            }`}
                        >
                            <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-tight">
                                <span className={leaveConflictCount > 0 ? 'text-rose-700 dark:text-rose-300' : 'text-gray-400 dark:text-gray-500'}>
                                    İzin Çakışması
                                </span>
                                <CalendarOff size={13} className={leaveConflictCount > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-gray-400'} />
                            </div>
                            <div className="flex items-baseline justify-between mt-1.5">
                                <span className={`text-xl font-black leading-none ${leaveConflictCount > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-gray-700 dark:text-gray-300'}`}>
                                    {leaveConflictCount}
                                </span>
                                <span className="text-[10px] text-gray-400 dark:text-gray-500 truncate ml-1">İzinli günde okutma</span>
                            </div>
                        </div>

                        {/* 2. Ardışık Giriş */}
                        <div
                            onClick={() => openModalWithAction(0)}
                            className="bg-gray-50/80 dark:bg-gray-900/40 p-3 rounded-xl border border-gray-100 dark:border-gray-700/50 flex flex-col justify-between transition-colors hover:border-amber-300 dark:hover:border-amber-800 cursor-pointer"
                        >
                            <div className="flex justify-between items-center text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase">
                                <span>Ardışık Giriş</span>
                                <ArrowUpRight size={13} className="text-amber-500" />
                            </div>
                            <div className="flex items-baseline justify-between mt-1.5">
                                <span className="text-xl font-black text-gray-800 dark:text-gray-100 leading-none">
                                    {summary.consecutiveEntryCount}
                                </span>
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">Çift giriş</span>
                            </div>
                        </div>

                        {/* 3. Ardışık Çıkış */}
                        <div
                            onClick={() => openModalWithAction(2)}
                            className="bg-gray-50/80 dark:bg-gray-900/40 p-3 rounded-xl border border-gray-100 dark:border-gray-700/50 flex flex-col justify-between transition-colors hover:border-amber-300 dark:hover:border-amber-800 cursor-pointer"
                        >
                            <div className="flex justify-between items-center text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase">
                                <span>Ardışık Çıkış</span>
                                <ArrowDownRight size={13} className="text-amber-500" />
                            </div>
                            <div className="flex items-baseline justify-between mt-1.5">
                                <span className="text-xl font-black text-gray-800 dark:text-gray-100 leading-none">
                                    {summary.consecutiveExitCount}
                                </span>
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">Çift çıkış</span>
                            </div>
                        </div>

                        {/* 4. Eksik Giriş */}
                        <div
                            onClick={() => openModalWithAction(4)}
                            className="bg-gray-50/80 dark:bg-gray-900/40 p-3 rounded-xl border border-gray-100 dark:border-gray-700/50 flex flex-col justify-between transition-colors hover:border-red-300 dark:hover:border-red-800 cursor-pointer"
                        >
                            <div className="flex justify-between items-center text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase">
                                <span>Eksik Giriş</span>
                                <Clock size={13} className="text-red-500" />
                            </div>
                            <div className="flex items-baseline justify-between mt-1.5">
                                <span className="text-xl font-black text-red-600 dark:text-red-400 leading-none">
                                    {summary.missingEntryCount}
                                </span>
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">Giriş yok</span>
                            </div>
                        </div>

                        {/* 5. Eksik Çıkış */}
                        <div
                            onClick={() => openModalWithAction(5)}
                            className="bg-gray-50/80 dark:bg-gray-900/40 p-3 rounded-xl border border-gray-100 dark:border-gray-700/50 flex flex-col justify-between transition-colors hover:border-red-300 dark:hover:border-red-800 cursor-pointer"
                        >
                            <div className="flex justify-between items-center text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase">
                                <span>Eksik Çıkış</span>
                                <Zap size={13} className="text-red-500" />
                            </div>
                            <div className="flex items-baseline justify-between mt-1.5">
                                <span className="text-xl font-black text-red-600 dark:text-red-400 leading-none">
                                    {summary.missingExitCount}
                                </span>
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">Çıkış yok</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer Action */}
            <div className="pt-2 border-t border-gray-100 dark:border-gray-700/60 flex justify-between items-center shrink-0">
                <div className="flex items-center gap-1.5 overflow-hidden">
                    {hasIssues && summary.topEmployees && summary.topEmployees.length > 0 && (
                        <div className="flex -space-x-1.5 overflow-hidden">
                            {summary.topEmployees.slice(0, 3).map((emp, i) => (
                                <div
                                    key={i}
                                    className="inline-block h-5 w-5 rounded-full ring-2 ring-white dark:ring-gray-800 bg-indigo-500 flex items-center justify-center text-[9px] font-bold text-white uppercase"
                                    title={emp.fullName}
                                >
                                    {emp.fullName.charAt(0)}
                                </div>
                            ))}
                        </div>
                    )}
                    <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate font-medium">
                        {hasIssues ? 'Toplu inceleme ve hızlı çözüm hazır' : 'Veri kalitesi %100 doğrulandı'}
                    </span>
                </div>
                <button
                    disabled={isEditMode}
                    onClick={() => openModalWithAction(leaveConflictCount > 0 ? 6 : 5)}
                    className={`inline-flex items-center gap-1 text-xs font-extrabold uppercase tracking-wider py-1 px-2.5 rounded-lg transition-all ${
                        isEditMode
                            ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                            : 'text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 active:scale-95'
                    }`}
                >
                    Detaylı İncele <ChevronRight size={14} className="stroke-[2.5]" />
                </button>
            </div>

            {isModalOpen && !isEditMode && (
                <BatchCorrectionModal
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false);
                        fetchSummary(date);
                        if (onActionComplete) onActionComplete(true);
                    }}
                    initialDate={date}
                    initialAction={initialModalAction}
                    onDateChange={(newDate) => setDate(newDate)}
                />
            )}
        </div>
    );
}

export default QualityDashboardWidget;
